import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * מכסת שעות לחודש אחד (מיגרציה 051).
 *
 * קיימת כדי שלא יצטרכו לשנות את המכסה הקבועה של העובד בגלל חודש
 * אחד חריג: מי שמוריד את המספר בגלל מילואים שוכח להחזיר אותו,
 * והעובד נשאר עם המכסה הזמנית לנצח.
 */
export type MonthBudget = {
  employee_id: string;
  month: string;
  weekly_hours: number | null;
  monthly_hours: number | null;
  note: string | null;
};

const KEY = ['month-budget'] as const;

export function useMonthBudget(employeeId: string | null, month: string) {
  return useQuery({
    queryKey: [...KEY, employeeId ?? 'none', month] as const,
    enabled: employeeId !== null,
    queryFn: async (): Promise<MonthBudget | null> => {
      const { data, error } = await supabase
        .from('employee_hour_budgets')
        .select('employee_id, month, weekly_hours, monthly_hours, note')
        .eq('employee_id', employeeId ?? '')
        .eq('month', month)
        .maybeSingle();
      if (error) throw error;
      return (data as MonthBudget | null) ?? null;
    },
  });
}

export function useMonthBudgetMutations() {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: KEY });
    // הסידור קורא את המכסה דרך schedule_hours
    void queryClient.invalidateQueries({ queryKey: ['schedule-hours'] });
  };

  const save = useMutation({
    mutationFn: async (input: {
      employee_id: string;
      month: string;
      weekly_hours: number | null;
      monthly_hours: number | null;
      note: string | null;
    }) => {
      const { error } = await supabase
        .from('employee_hour_budgets')
        .upsert({ ...input, updated_at: new Date().toISOString() });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  /** חזרה למכסה הקבועה של העובד: מוחקים את הדריסה, לא מאפסים. */
  const clear = useMutation({
    mutationFn: async (input: { employee_id: string; month: string }) => {
      const { error } = await supabase
        .from('employee_hour_budgets')
        .delete()
        .eq('employee_id', input.employee_id)
        .eq('month', input.month);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  return { save, clear };
}
